/**
 * The theme choice: Light, Dark, or follow the system.
 *
 * WHERE THE WORK ACTUALLY HAPPENS
 * -------------------------------
 * Most of it is NOT here. The choice is applied by a blocking inline script in
 * `index.html`, before the stylesheet loads and before the first paint — that
 * is the only way to stop a reader who has chosen Dark seeing a white flash on
 * every navigation. This module is what changes the choice afterwards and keeps
 * the two in step.
 *
 * THREE STATES, AND "SYSTEM" IS AN ABSENCE
 * ----------------------------------------
 * `light` and `dark` set `data-theme` on the root element. **`system` REMOVES
 * it.** The `prefers-color-scheme` rules in `base.css` are written as
 * `:root:not([data-theme='light'])`, so they apply exactly when no explicit
 * choice is present. Writing `data-theme="system"` would match neither the
 * light branch nor the dark one and would pin the app to light for anybody on a
 * dark desktop — a bug that looks like the feature working.
 *
 * The stored value is the reader's CHOICE, not the resulting appearance. Two
 * people who both look dark today can have chosen differently, and only one of
 * them should change when the sun goes down.
 */

export const THEME_CHOICES = ['light', 'dark', 'system'] as const;
export type ThemeChoice = (typeof THEME_CHOICES)[number];

const KEY = 'fitout:theme';

/** The word shown in the menu. Sentence case, and "Dark mode", never "Night". */
export const THEME_LABELS: Readonly<Record<ThemeChoice, string>> = {
  light: 'Light',
  dark: 'Dark',
  system: 'Use system setting',
};

function isChoice(value: string | null): value is ThemeChoice {
  return value === 'light' || value === 'dark' || value === 'system';
}

/**
 * What the reader has chosen. `system` when they have not chosen anything —
 * following the machine is the honest default, not a preference we invented.
 */
export function readTheme(): ThemeChoice {
  try {
    const stored = window.localStorage.getItem(KEY);
    return isChoice(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

/**
 * Apply a choice and remember it.
 *
 * Storage is wrapped but the ATTRIBUTE is set either way: a browser with
 * storage disabled should still be able to switch theme for this session. The
 * preference is what is lost, not the ability to choose.
 */
export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', choice);

  try {
    window.localStorage.setItem(KEY, choice);
  } catch {
    /* a preference is not worth an error */
  }
}

/**
 * Which theme is actually on screen right now.
 *
 * Needed because "Use system setting" does not say what the reader is looking
 * at, and a control that offers to switch has to know. Reads the live media
 * query rather than a stored guess.
 */
export function resolvedTheme(choice: ThemeChoice = readTheme()): 'light' | 'dark' {
  if (choice !== 'system') return choice;
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}
